import React, { useRef, useState } from "react";
import axios from "axios";
import SEO from "../components/SEO"; // SEO Component Import
import {
  Mail,
  Phone,
  Send,
  ShieldCheck,
  Clock,
  CheckCircle,
  Loader2,
  Wallet,
  Percent,
  Globe,
  AlertTriangle,
  XCircle,
  Hash
} from "lucide-react";

const Contact = () => {
  const form = useRef();
  const [isSending, setIsSending] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  const [refundPreview, setRefundPreview] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [orderError, setOrderError] = useState(null);

  const handleOrderCheck = async (e) => {
    const orderId = e.target.value.trim();
    if (!orderId || orderId.length < 5) return;

    setIsValidating(true);
    try {
      const res = await axios.get(`https://wethrift-backend.onrender.com/api/orders/check/${orderId}`);
      const order = res.data.order;

      if (order.payment_method === "COD") {
        setOrderError("COD_REJECTED: Automated returns disabled for COD.");
        setRefundPreview(null);
      } else {
        const deduction = order.amount * 0.30;
        setRefundPreview({
          original: order.amount,
          deduction: deduction.toFixed(2),
          final: (order.amount - deduction).toFixed(2),
        });
        setOrderError(null);
      }
    } catch (err) {
      setOrderError("NOT_FOUND: Check ID Integrity.");
      setRefundPreview(null);
    } finally {
      setIsValidating(false);
    }
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    if (orderError) return alert("System Error: Order ID Invalid.");
    
    setIsSending(true);
    const formData = {
      user_name: form.current.user_name.value,
      user_email: form.current.user_email.value,
      order_id: form.current.order_id.value,
      message: form.current.message.value,
      refund_estimate: refundPreview ? refundPreview.final : 0,
    };

    try {
      const response = await axios.post("https://wethrift-backend.onrender.com/api/contact/send-email", formData);
      if (response.data.success) {
        setIsSuccess(true);
        setRefundPreview(null);
        form.current.reset();
        setTimeout(() => setIsSuccess(false), 5000);
      }
    } catch (error) {
      alert("CRITICAL_ERROR: Transmission Interrupted.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-black text-zinc-900 dark:text-white pt-24 pb-20 px-4 font-sans selection:bg-emerald-500 selection:text-black transition-colors duration-300">
      {/* SEO IMPLEMENTATION */}
      <SEO 
        title="Return Terminal | Protocol 01"
        description="Initiate high-speed returns and refund audits. Automated system for WE THRIFT ARCHIVE assets. 72-hour reversal window."
        url="/contact"
      />

      <div className="max-w-7xl mx-auto space-y-16">
        
        {/* ================= HERO SECTION ================= */}
        <div className="text-center space-y-6 animate-in fade-in slide-in-from-top duration-1000">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/5 text-[9px] uppercase tracking-[0.4em] font-black text-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.1)]">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            System_Status: Online
          </div>

          <h1 className="text-4xl md:text-8xl font-black italic uppercase tracking-tighter leading-none">
            REVERSAL <span className="text-emerald-500 underline decoration-zinc-200 dark:decoration-white/10 underline-offset-8">PROTOCOL</span>
          </h1>

          <p className="text-[10px] md:text-xs text-zinc-500 uppercase font-bold tracking-[0.2em] max-w-xl mx-auto leading-relaxed">
            Automated Audit System for Archive Assets. Submit your request for instant validation and wallet credit reversal.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-6 max-w-4xl mx-auto">
            <Stat icon={<Clock size={16} />} title="Audit" value="24H" />
            <Stat icon={<Wallet size={16} />} title="Method" value="Wallet" />
            <Stat icon={<Percent size={16} />} title="Tax" value="30%" />
            <Stat icon={<ShieldCheck size={16} />} title="Security" value="AES-256" />
          </div>
        </div>

        {/* ================= MAIN INTERFACE ================= */}
        <div className="grid lg:grid-cols-12 gap-10">
          
          {/* INFO SIDEBAR */}
          <div className="lg:col-span-4 space-y-6 order-2 lg:order-1">
            <InfoRow icon={<Mail size={18} />} label="Support_Node" value="ops@wethrift.com" />
            <InfoRow icon={<Globe size={18} />} label="Grid_Location" value="MUMBAI_HQ" />

            <div className="p-8 rounded-3xl bg-emerald-500 text-black space-y-4 shadow-[0_20px_50px_rgba(16,185,129,0.2)]">
                <AlertTriangle size={24} />
                <h3 className="text-2xl font-black italic uppercase leading-tight">Critical<br/>Notice.</h3>
                <p className="text-[10px] font-black uppercase tracking-widest leading-loose opacity-80">
                  Automated returns are strictly restricted to prepaid acquisitions. Manual override required for COD disputes.
                </p>
            </div>
          </div>

          {/* RETURN FORM */}
          <div className="lg:col-span-8 relative order-1 lg:order-2">
            <div className="bg-zinc-100 dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800 rounded-[2.5rem] p-6 md:p-12 backdrop-blur-3xl shadow-2xl relative overflow-hidden transition-colors">
              
              {isSuccess && (
                <div className="absolute inset-0 bg-white dark:bg-black z-50 flex flex-col items-center justify-center text-center p-6 transition-colors">
                  <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(16,185,129,0.5)]">
                    <CheckCircle size={32} className="text-black" />
                  </div>
                  <h3 className="text-3xl font-black uppercase italic text-zinc-900 dark:text-white">Transmission_Success</h3>
                  <p className="text-[10px] text-emerald-500 uppercase tracking-widest mt-4 font-black">Audit in Progress... Check Wallet in 24h.</p>
                </div>
              )}

              <form ref={form} onSubmit={sendEmail} className="space-y-8">
                <div className="grid md:grid-cols-2 gap-8">
                  <InputField name="user_name" label="Identity_Name" placeholder="ENTER FULL NAME" />
                  
                  <div className="relative group">
                    <InputField 
                      name="order_id" 
                      label="Asset_ID" 
                      placeholder="WTH-XXXXX" 
                      onBlur={handleOrderCheck}
                    />
                    {isValidating && <Loader2 size={16} className="absolute right-0 bottom-4 animate-spin text-emerald-500" />}
                    {orderError && (
                      <p className="absolute -bottom-5 left-0 text-[8px] font-black text-red-500 uppercase tracking-[0.2em] flex items-center gap-1">
                        <XCircle size={10} /> {orderError}
                      </p>
                    )}
                  </div>
                </div>

                {refundPreview && (
                  <div className="p-6 bg-emerald-500/5 rounded-3xl border border-dashed border-emerald-500/30 animate-in zoom-in-95">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-[9px] font-black uppercase tracking-[0.3em] text-emerald-500/60 italic">Audit_Preview</span>
                      <span className="px-2 py-0.5 bg-emerald-500 text-black text-[8px] font-black uppercase rounded">Eligible</span>
                    </div>
                    <div className="flex justify-between text-[11px] font-black uppercase tracking-widest text-zinc-500 dark:text-zinc-400">
                      <span>Value: ₹{refundPreview.original}</span>
                      <span className="text-red-500">Processing: -₹{refundPreview.deduction}</span>
                    </div>
                    <div className="mt-4 pt-4 border-t border-zinc-200 dark:border-white/5 flex justify-between items-end">
                       <p className="text-[9px] font-black uppercase tracking-widest text-zinc-500">Net_Return_Credit</p>
                       <p className="text-3xl font-black text-emerald-500 tracking-tighter">₹{refundPreview.final}</p>
                    </div>
                  </div>
                )}

                <InputField name="user_email" type="email" label="Contact_Node" placeholder="USER@DOMAIN.COM" />
                <TextArea name="message" label="Decline_Reason" placeholder="DESCRIBE ASSET DEFECTS OR SIZING ISSUES..." />

                <button
                  disabled={isSending || !!orderError}
                  type="submit"
                  className={`group relative w-full py-6 rounded-2xl font-black text-[11px] uppercase tracking-[0.5em] flex items-center justify-center gap-4 transition-all duration-500 ${
                    isSending || orderError
                      ? "bg-zinc-200 dark:bg-zinc-800 cursor-not-allowed text-zinc-400 dark:text-zinc-500"
                      : "bg-black dark:bg-white text-white dark:text-black hover:bg-emerald-500 dark:hover:bg-emerald-500 hover:text-white dark:hover:text-black hover:shadow-[0_0_30px_rgba(16,185,129,0.3)] active:scale-95"
                  }`}
                >
                  {isSending ? <Loader2 size={18} className="animate-spin" /> : (
                    <>Submit_Protocol <Send size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" /></>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ================= COMPONENTS ================= */

const InputField = ({ label, placeholder, name, type = "text", onBlur }) => (
  <div className="group space-y-3">
    <label className="text-[9px] uppercase tracking-[0.4em] font-black text-zinc-400 dark:text-zinc-500 group-focus-within:text-emerald-500 transition-colors">
      {label}
    </label>
    <input
      name={name} type={type} required placeholder={placeholder} onBlur={onBlur}
      className="w-full bg-transparent border-b border-zinc-200 dark:border-zinc-800 py-3 outline-none focus:border-emerald-500 text-xs font-bold tracking-widest uppercase transition-all placeholder:text-zinc-300 dark:placeholder:text-zinc-800 text-zinc-900 dark:text-white"
    />
  </div>
);

const TextArea = ({ label, placeholder, name }) => (
  <div className="group space-y-3">
    <label className="text-[9px] uppercase tracking-[0.4em] font-black text-zinc-400 dark:text-zinc-500 group-focus-within:text-emerald-500 transition-colors">
      {label}
    </label>
    <textarea
      name={name} rows="3" required placeholder={placeholder}
      className="w-full bg-transparent border-b border-zinc-200 dark:border-zinc-800 py-3 outline-none focus:border-emerald-500 text-xs font-bold tracking-widest uppercase resize-none transition-all placeholder:text-zinc-300 dark:placeholder:text-zinc-800 text-zinc-900 dark:text-white"
    />
  </div>
);

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-center gap-5 p-5 rounded-2xl border border-zinc-100 dark:border-zinc-900 bg-zinc-50 dark:bg-zinc-900/20 hover:border-emerald-500/50 transition-all duration-500 group">
    <div className="w-10 h-10 rounded-xl bg-white dark:bg-zinc-800 shadow-sm dark:shadow-none flex items-center justify-center text-zinc-500 group-hover:bg-emerald-500 group-hover:text-black transition-all">
      {icon}
    </div>
    <div>
      <p className="text-[8px] uppercase tracking-[0.3em] font-black text-zinc-400 dark:text-zinc-600 italic">{label}</p>
      <p className="text-[11px] font-black text-zinc-900 dark:text-white uppercase">{value}</p>
    </div>
  </div>
);

const Stat = ({ icon, title, value }) => (
  <div className="p-4 md:p-6 rounded-2xl border border-zinc-100 dark:border-zinc-900 text-center space-y-2 bg-white dark:bg-zinc-900/40 shadow-sm dark:shadow-none backdrop-blur-md">
    <div className="text-emerald-500 mb-1 flex justify-center opacity-70">{icon}</div>
    <p className="text-[8px] uppercase tracking-widest font-black text-zinc-400 dark:text-zinc-600">{title}</p>
    <p className="text-[10px] font-black text-zinc-900 dark:text-white uppercase italic">{value}</p>
  </div>
);

export default Contact;